/**
 * Shared chrome for the three learner-insight portals so they read as one place:
 * a lens switcher (Teacher / Student / Parent) and a learner picker. All three
 * lenses are driven by the same class roster (mockData.ts / DEMO_CLASS).
 */

import { Link } from '@tanstack/react-router';
import { GraduationCap, Sparkles, Users } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ClassRoster } from './mockData';

type Lens = 'teacher' | 'student' | 'parent';
const LENSES: { key: Lens; label: string; icon: typeof GraduationCap }[] = [
  { key: 'teacher', label: 'Teacher', icon: GraduationCap },
  { key: 'student', label: 'Student', icon: Sparkles },
  { key: 'parent', label: 'Parent', icon: Users },
];
const tab = (isActive: boolean) => cn(
  'inline-flex items-center gap-1.5 rounded-[9px] px-3.5 py-2 text-[13px] font-semibold transition-colors',
  isActive ? 'bg-white text-[#1B1826] shadow-sm dark:bg-white/10 dark:text-white' : 'text-[#8A8593] hover:text-[#1B1826] dark:hover:text-white',
);

/**
 * Route-linked lens switcher shown on all three portal pages (teacher / student /
 * parent) so navigation is uniform in every direction. `learnerId`, when given,
 * is carried to the student & parent pages so switching lens keeps the same learner.
 */
export function LensTabs({ active, learnerId }: { active: Lens; learnerId?: string }) {
  const search = { learner: learnerId };
  return (
    <div className="flex gap-[3px] rounded-[13px] border border-black/10 bg-black/[0.03] p-1 dark:border-white/10 dark:bg-white/[0.05]">
      <Link to="/learn/teacher" className={tab(active === 'teacher')}>
        <GraduationCap className="h-[15px] w-[15px]" /><span className="hidden sm:inline">Teacher</span>
      </Link>
      <Link to="/learn/student" search={search} className={tab(active === 'student')}>
        <Sparkles className="h-[15px] w-[15px]" /><span className="hidden sm:inline">Student</span>
      </Link>
      <Link to="/learn/parent" search={search} className={tab(active === 'parent')}>
        <Users className="h-[15px] w-[15px]" /><span className="hidden sm:inline">Parent</span>
      </Link>
    </div>
  );
}

/** In-place lens switcher (state-driven) used inside the teacher dashboard drill-in. */
export function LensSwitch({ active, onChange }: { active: Lens; onChange: (l: Lens) => void }) {
  return (
    <div className="inline-flex gap-[3px] rounded-[13px] border border-black/10 bg-black/[0.03] p-1 dark:border-white/10 dark:bg-white/[0.05]">
      {LENSES.map(({ key, label, icon: Icon }) => (
        <button key={key} onClick={() => onChange(key)}
          className={cn('inline-flex items-center gap-1.5 rounded-[9px] px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors',
            active === key ? 'bg-white text-[#1B1826] shadow-sm dark:bg-white/10 dark:text-white' : 'text-[#8A8593] hover:text-[#1B1826] dark:hover:text-white')}>
          <Icon className="h-[14px] w-[14px]" />{label === 'Teacher' ? 'Teacher view' : label}
        </button>
      ))}
    </div>
  );
}

export function LearnerPicker({ roster, value, onChange }: { roster: ClassRoster; value: string; onChange: (id: string) => void }) {
  const learners = [...roster.learners].sort((a, b) => Number(a.rollNo) - Number(b.rollNo));
  return (
    <label className="flex items-center gap-2.5 rounded-xl border border-black/10 bg-white px-3.5 py-2 dark:border-white/10 dark:bg-white/[0.04]">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8593]">Learner</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="max-w-[200px] truncate bg-transparent text-[13px] font-medium outline-none">
        {learners.map((l) => <option key={l.id} value={l.id}>{l.firstName} · Roll {l.rollNo}</option>)}
      </select>
    </label>
  );
}

export type { Lens };
